import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import AnalyticCard from '../CommonComponents/AnalyticCard';
import ThreeDots from 'packages/novous_web/src/assets/icons/menu.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import FleetDistanceIcon from 'packages/novous_web/src/assets/icons/fleetdistance.svg';
import InfoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import { Box, Tooltip } from '@mui/material';
import CommonGaugeMeterChart from '../CommonComponents/CommonGaugeMeterChart';
import BarChartCommon from '../CommonComponents/BarChartCommon';
import ProductivityHeatMapChart from '../CommonComponents/ProductivityHeatMapChart';
import CommonDoublePieChart from '../CommonComponents/CommonDoublePieChart';
import CommonScatterChart from '../CommonComponents/CommonScatterChart';
import DayNightFloatingGraph from '../CommonComponents/DayNightFloatingGraph';
import CommonFloatingGraph from '../CommonComponents/CommonFloationgGraph';
import DailyTimeBreakdownChart from './DailyTimeBreakdownChart';

interface AnalyticsSectionsConfig {
  [key: string]: boolean | undefined;
}

interface ProductivityAnalyticsProps {
  apiData: { [key: string]: any };
  chartConfigs: any[];
  filters: any;
  sectionsConfig?: AnalyticsSectionsConfig;
  onCardDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;

  productivityAnalyticsCardData?: { [key: number]: any };
  productivityAnalyticsCardPaginationInfo?: { [key: number]: any };
  onFetchSingleTableData?: (tableId: number) => Promise<any>;
  onProductivityAnalyticsCardTablePagination?: (tableId: number, cursor?: any) => void;
  // Paginated response for Chart 1105 (Frequent Violators details)
  paginatedData1105?: any;
  onPaginationChange1105?: (cursor?: any) => void;
  // Paginated response for Chart 1203 (Frequent Violators details)
  paginatedData1203?: any;
  onPaginationChange1203?: (cursor?: any) => void;
  // Paginated response for Chart 1604 (Frequent Violators details)
  paginatedData1604?: any;
  onPaginationChange1604?: (cursor?: any) => void;
  // Paginated response for Chart 1604 (Frequent Violators details)
  paginatedData1608?: any;
  onPaginationChange1608?: (cursor?: any) => void;
}

const ProductivityAnalytics: React.FC<ProductivityAnalyticsProps> = ({
  apiData,
  chartConfigs,
  filters,
  sectionsConfig = {},
  onCardDataUpdate,

  productivityAnalyticsCardData = {},
  productivityAnalyticsCardPaginationInfo = {},
  onFetchSingleTableData,
  onProductivityAnalyticsCardTablePagination,
  paginatedData1105,
  onPaginationChange1105,
  paginatedData1203,
  onPaginationChange1203,
  paginatedData1604,
  onPaginationChange1604,
  paginatedData1608,
  onPaginationChange1608,
}) => {
  const productivityCardTableIdMap: { [key: number]: { title: string; tableId: number } } = {
    1100: { title: 'Fleet Distance Travelled', tableId: 1500 },
    1101: { title: 'Average Median Speed', tableId: 1501 },
    1058: { title: 'Driving Time', tableId: 1502 },
    1068: { title: 'Total Field Time', tableId: 1503 },
    1073: { title: 'Low Speeding Hours', tableId: 1504 },
    1072: { title: 'Eco Driving Hours', tableId: 1505 },
    1071: { title: 'Driver Productivity Index', tableId: 1321 },
    1203: { title: 'Most Utilised Vehicles', tableId: 1511 },
    1300: { title: 'Idle Time by Vehicle', tableId: 1512 },
    1303: { title: ' Distance Contribution by Vehicle & Date (in KM)', tableId: 1513 },
    1057: { title: 'Day and Night Driving Hours', tableId: 1514 },
    1076: { title: 'Eco-Driving and Low-Speed Comparision', tableId: 1515 },
    1055: { title: 'Daily vehicle usage (Day vs Night)', tableId: 1516 },
    1304: { title: 'Daily Eco-Driving Hours per Vehicle', tableId: 1517 },
    1066: { title: 'Productivity and Downtime', tableId: 1518 },
    1074: { title: 'Driving Hours Breakdown', tableId: 1520 },
    1070: { title: 'Daily time breakdown by vehicle', tableId: 1519 },
  };

  const transformProductivityAnalyticsCardData = useCallback(
    (tableId: number) => {
      const responseData = productivityAnalyticsCardData[tableId];

      console.log(`[Transform Data] TableID: ${tableId}, ResponseData:`, responseData);

      if (!responseData || !responseData.data || responseData.data.length === 0) {
        console.log(`[Transform Data] No data found for tableId ${tableId}`);
        return [];
      }

      console.log(`[Transform Data] Found ${responseData.data.length} records for tableId ${tableId}`);

      // Transform based on table ID and its structure
      return responseData.data.map((item: any) => ({
        vehicleId: item.vehicle_id || '',
        recordDate: item.record_date || '',
        distanceKm:
          item.distance_km !== undefined && item.distance_km !== null
            ? parseFloat(item.distance_km).toFixed(2)
            : item.total_km_driven !== undefined && item.total_km_driven !== null
            ? parseFloat(item.total_km_driven).toFixed(2)
            : '0',
        medianSpeed:
          item.median_speed !== undefined && item.median_speed !== null
            ? parseFloat(item.median_speed).toFixed(2)
            : item.median_gps_speed !== undefined && item.median_gps_speed !== null
            ? parseFloat(item.median_gps_speed).toFixed(2)
            : '0',
        drivingTime:
          item.driving_time !== undefined && item.driving_time !== null
            ? parseFloat(item.driving_time).toFixed(2)
            : item.total_hours !== undefined && item.total_hours !== null
            ? parseFloat(item.total_hours).toFixed(2)
            : '0',
        totalFieldTime:
          item.total_field_time !== undefined && item.total_field_time !== null
            ? parseFloat(item.total_field_time).toFixed(2)
            : '0',
        lowSpeedingHours:
          item.low_speeding_hours !== undefined && item.low_speeding_hours !== null
            ? parseFloat(item.low_speeding_hours).toFixed(2)
            : item.low_speed_hours !== undefined && item.low_speed_hours !== null
            ? parseFloat(item.low_speed_hours).toFixed(2)
            : '0',
        lowSpeedHours:
          item.low_speed_hours !== undefined && item.low_speed_hours !== null
            ? parseFloat(item.low_speed_hours).toFixed(2)
            : item.low_speeding_hours !== undefined && item.low_speeding_hours !== null
            ? parseFloat(item.low_speeding_hours).toFixed(2)
            : '0',
        ecoDrivingHours:
          item.eco_driving_hours !== undefined && item.eco_driving_hours !== null
            ? parseFloat(item.eco_driving_hours).toFixed(2)
            : '0',
        productivityScore:
          item.productivity_score !== undefined && item.productivity_score !== null
            ? parseFloat(item.productivity_score).toFixed(2)
            : '0',
        statisticalProductivityScore:
          item.statistical_productivity_score !== undefined && item.statistical_productivity_score !== null
            ? parseFloat(item.statistical_productivity_score).toFixed(2)
            : item.utilization_percentage !== undefined && item.utilization_percentage !== null
            ? parseFloat(item.utilization_percentage).toFixed(2)
            : '0',
        idleTime:
          item.idle_time !== undefined && item.idle_time !== null
            ? parseFloat(item.idle_time).toFixed(2)
            : '0',
        idleTimeHours:
          item.idle_time_hours !== undefined && item.idle_time_hours !== null
            ? parseFloat(item.idle_time_hours).toFixed(2)
            : '0',
        hoursUsed:
          item.hours_used !== undefined && item.hours_used !== null
            ? parseFloat(item.hours_used).toFixed(2)
            : item.utilisation_time !== undefined && item.utilisation_time !== null
            ? parseFloat(item.utilisation_time).toFixed(2)
            : '0',
        totalKmDriven:
          item.total_km_driven !== undefined && item.total_km_driven !== null
            ? parseFloat(item.total_km_driven).toFixed(2)
            : item.distance_km !== undefined && item.distance_km !== null
            ? parseFloat(item.distance_km).toFixed(2)
            : item.ditance_covered_km !== undefined && item.ditance_covered_km !== null
            ? parseFloat(item.ditance_covered_km).toFixed(2)
            : '0',
        dayDrivingHours:
          item.day_driving_hours !== undefined && item.day_driving_hours !== null
            ? parseFloat(item.day_driving_hours).toFixed(2)
            : item.day_drving_hours !== undefined && item.day_drving_hours !== null
            ? parseFloat(item.day_drving_hours).toFixed(2)
            : item.day_hours !== undefined && item.day_hours !== null
            ? parseFloat(item.day_hours).toFixed(2)
            : '0',
        nightDrivingHours:
          item.night_driving_hours !== undefined && item.night_driving_hours !== null
            ? parseFloat(item.night_driving_hours).toFixed(2)
            : item.night_hours !== undefined && item.night_hours !== null
            ? parseFloat(item.night_hours).toFixed(2)
            : '0',
        dayVsNightRatio:
          item.day_vs_night_ratio !== undefined && item.day_vs_night_ratio !== null
            ? parseFloat(item.day_vs_night_ratio).toFixed(2)
            : '0',
        utilizationPercentage:
          item.utilization_percentage !== undefined && item.utilization_percentage !== null
            ? parseFloat(item.utilization_percentage).toFixed(2)
            : '0',
        utilisationTime:
          item.utilisation_time !== undefined && item.utilisation_time !== null
            ? parseFloat(item.utilisation_time).toFixed(2)
            : '0',
        distanceCoveredKm:
          item.ditance_covered_km !== undefined && item.ditance_covered_km !== null
            ? parseFloat(item.ditance_covered_km).toFixed(2)
            : '0',
        totalDrivingHours:
          item.total_driving_hours !== undefined && item.total_driving_hours !== null
            ? parseFloat(item.total_driving_hours).toFixed(2)
            : '0',
        totalActiveVehicles:
          item.total_active_vehicles !== undefined && item.total_active_vehicles !== null
            ? item.total_active_vehicles.toString()
            : '0',
        ecoDrivingPercentage:
          item.eco_driving_percentage !== undefined && item.eco_driving_percentage !== null
            ? Math.floor(item.eco_driving_percentage).toString()
            : '0',
        productivityScorePercentage:
          item.productivity_score_percentage !== undefined && item.productivity_score_percentage !== null
            ? Math.floor(item.productivity_score_percentage).toString()
            : '0',
        idleTimePercentage:
          item.idle_time_percentage !== undefined && item.idle_time_percentage !== null
            ? Math.floor(item.idle_time_percentage).toString()
            : '0',
      }));
    },
    [productivityAnalyticsCardData],
  );

  // Transform pagination info from API response format to component expected format
  const transformPaginationInfo = useCallback(
    (tableId: number) => {
      const responseData = productivityAnalyticsCardData[tableId];

      if (!responseData) {
        return {
          lastEvaluatedKey: null,
          hasMorePages: false,
        };
      }

      // API returns next_cursor, but component expects lastEvaluatedKey
      return {
        lastEvaluatedKey: responseData.next_cursor || null,
        hasMorePages: !!responseData.next_cursor, // If next_cursor exists, there are more pages
      };
    },
    [productivityAnalyticsCardData],
  );

  // Cursor storage for Most Utilised Vehicles table pagination (matches AnalyticsCompilance pattern)
  const [mostUtilisedCursors, setMostUtilisedCursors] = useState<any>({});

  const getProductivityTableColumns = (chartId: number) => {
    const baseColumns = [
      { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
      { key: 'recordDate', label: 'Date', align: 'left' as const },
    ];

    // Add specific columns based on chart ID
    switch (chartId) {
      case 1100: // Fleet Distance Travelled
        return [...baseColumns, { key: 'distanceKm', label: 'Distance (Km)', align: 'left' as const }];
      case 1101: // Average Median Speed
        return [
          ...baseColumns,
          { key: 'medianSpeed', label: 'Average Median Speed (Km/hr)', align: 'left' as const },
        ];
      case 1058: // Driving Time
        return [
          ...baseColumns,
          { key: 'drivingTime', label: 'Driving Time (hours)', align: 'left' as const },
        ];
      case 1068: // Total field time
        return [
          ...baseColumns,
          { key: 'totalFieldTime', label: 'Total Field Time (hours)', align: 'left' as const },
        ];
      case 1073: // Low Speeding Hours
        return [
          ...baseColumns,
          { key: 'lowSpeedingHours', label: 'Low Speeding Hours', align: 'left' as const },
        ];
      case 1072: // Eco Driving Hours
        return [
          ...baseColumns,
          { key: 'ecoDrivingHours', label: 'Eco Driving Hours', align: 'left' as const },
        ];
      case 1071: // Driver Productivity Index
        return [
          ...baseColumns,
          { key: 'productivityScore', label: 'Productivity Score (%)', align: 'left' as const },
        ];
      case 1203: // Most Utilised Vehicles
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'utilizationPercentage', label: 'Utilization (%)', align: 'left' as const },
          { key: 'utilisationTime', label: 'Utilisation Time (hrs)', align: 'left' as const },
          { key: 'distanceCoveredKm', label: 'Distance (Km)', align: 'left' as const },
        ];
      case 1300: // Idle Time by Vehicle
        return [...baseColumns, { key: 'idleTime', label: 'Idle Time (hours)', align: 'left' as const }];
      case 1303: //  Distance Contribution by Vehicle & Date (in KM)
        return [...baseColumns, { key: 'totalKmDriven', label: 'Distance (Km)', align: 'left' as const }];
      case 1057: // Day and Night Driving Hours
        return [
          ...baseColumns,
          { key: 'dayDrivingHours', label: 'Day Driving Hours', align: 'left' as const },
          { key: 'nightDrivingHours', label: 'Night Driving Hours', align: 'left' as const },
        ];
      case 1076: // Eco-Driving and Low-Speed Comparision
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'hoursUsed', label: 'Hours Used', align: 'left' as const },
          { key: 'ecoDrivingHours', label: 'Eco Driving Hours', align: 'left' as const },
          { key: 'lowSpeedHours', label: 'Low Speed Hours', align: 'left' as const },
        ];
      case 1066: // Productivity and Downtime
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'hoursUsed', label: 'Hours Used', align: 'left' as const },
          { key: 'idleTime', label: 'Idle Time', align: 'left' as const },
          { key: 'distanceKm', label: 'Distance (Km)', align: 'left' as const },
          { key: 'productivityScorePercentage', label: 'Productivity %', align: 'left' as const },
          { key: 'idleTimePercentage', label: 'Idle Time %', align: 'left' as const },
        ];
      case 1055: // Daily vehicle usage (Day vs Night)
        return [
          { key: 'recordDate', label: 'Date', align: 'left' as const },
          { key: 'dayDrivingHours', label: 'Day Hours', align: 'left' as const },
          { key: 'nightDrivingHours', label: 'Night Hours', align: 'left' as const },
          { key: 'totalDrivingHours', label: 'Total Driving Hours', align: 'left' as const },
          { key: 'totalActiveVehicles', label: 'Active Vehicles', align: 'left' as const },
        ];
      case 1304: // Daily Eco-Driving Hours per Vehicle
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'recordDate', label: 'Date', align: 'left' as const },
          { key: 'ecoDrivingHours', label: 'Eco Driving Hours', align: 'left' as const },
          { key: 'totalDrivingHours', label: 'Total Driving Hours', align: 'left' as const },
          { key: 'ecoDrivingPercentage', label: 'Eco Driving %', align: 'left' as const },
        ];
      case 1074: // Driving Hours Breakdown
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'hoursUsed', label: 'Hours Used', align: 'left' as const },
          { key: 'ecoDrivingHours', label: 'Eco Driving Hours', align: 'left' as const },
          { key: 'lowSpeedHours', label: 'Low Speed Hours', align: 'left' as const },
        ];
      case 1070: // Daily time breakdown by vehicle
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'recordDate', label: 'Date', align: 'left' as const },
          { key: 'hoursUsed', label: 'Hours Used', align: 'left' as const },
          { key: 'idleTime', label: 'Idle Time', align: 'left' as const },
          { key: 'totalFieldTime', label: 'Total Field Time', align: 'left' as const },
        ];
      default:
        return baseColumns;
    }
  };

  // Process cards data from centralized API data
  const cardsData = useMemo(() => {
    return chartConfigs.map((item) => {
      const chartData = apiData[item.id];

      if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
        const getUnit = (title: string) => {
          switch (title) {
            case 'Fleet Distance Travelled':
              return 'Km';
            case 'Average Median Speed':
              return 'Km/hr';
            case 'Driving Time':
              return 'hrs';
            case 'Total Field Time':
              return 'hrs';
            case 'Low Speeding Hours':
              return 'hrs';
            case 'Eco Driving Hours':
              return 'hrs';
            default:
              return '';
          }
        };

        return {
          icon: item.icon,
          title: item.title,
          value: '0.00',
          unit: getUnit(item.title),
          chartData: [],
          chartId: item.id, // Add this line
        };
      }

      if (item.id === 1071) {
        const data = chartData.data[0];
        const complianceScore = data.productivity_score || 0;

        return {
          icon: item.icon,
          title: item.title,
          value: `${complianceScore}%`,
          unit: '',
          chartData: [],
          complianceScore: complianceScore,
          chartId: item.id, // Add this line
        };
      }

      const sorted = chartData.data.sort((a: any, b: any) => {
        if (a.record_date && b.record_date) {
          return new Date(a.record_date).getTime() - new Date(b.record_date).getTime();
        }
        return 0;
      });

      const getValue = (d: any) => {
        for (const key in d) {
          if (typeof d[key] === 'number') {
            return d[key];
          }
        }
        return 0;
      };

      const lastValue = getValue(sorted[sorted.length - 1]);

      const getUnit = (title: string) => {
        switch (title) {
          case 'Fleet Distance Travelled':
            return 'Km';
          case 'Average Median Speed':
            return 'Km/hr';
          case 'Driving Time':
            return 'hrs';
          case 'Total Field Time':
            return 'hrs';
          case 'Low Speeding Hours':
            return 'hrs';
          case 'Eco Driving Hours':
            return 'hrs';
          default:
            return '';
        }
      };

      return {
        icon: item.icon,
        title: item.title,
        value: lastValue.toFixed(2),
        unit: getUnit(item.title),
        chartData: sorted.map((d: any) => ({
          x: d.record_date ? new Date(d.record_date).toLocaleDateString('en-GB') : '',
          y: getValue(d),
        })),
        chartId: item.id, // Add this line
      };
    });
  }, [apiData, chartConfigs]);

  // Split cards into top 4 and bottom 4
  const topCards = cardsData.slice(0, 4);
  const bottomCards = cardsData.slice(4, 6);

  const complianceScore = useMemo(() => {
    const complianceCard = cardsData.find((card) => card.title === 'Driver Productivity Index');
    return complianceCard && typeof complianceCard.complianceScore === 'number'
      ? complianceCard.complianceScore
      : 0;
  }, [cardsData]);

  const getProductivityTooltipMessage = (title: string): string => {
    const messages: { [key: string]: string } = {
      'Fleet Distance Travelled': 'Total distance travelled by all vehicles during the selected period.',
      'Average Median Speed':
        'Middle speed value of all recorded speeds, calculated using the median formula.',
      'Driving Time': 'Total time the fleet spent driving (engine on and vehicle moving).',
      'Total Field Time': 'Total operational time of the fleet, including both driving and idle time.',
      'Low Speeding Hours': 'Total hours where vehicles were driven at speeds ≤ 20 km/h.',
      'Eco Driving Hours':
        'Total hours where vehicles were driven within the optimal speed range of 45–70 km/h.',
      'Driver Productivity Index':
        'A weighted score (0–100) measuring vehicle productivity based on utilization, eco driving, idle time, and overspeeding behaviour.',
      'Most Utilised Vehicles':
        'Vehicles ranked by utilization percentage (driving time divided by total field time).',
      'Idle Time by Vehicle': 'Daily idle time comparison for the top 7 vehicles.',
      ' Distance Contribution by Vehicle & Date (in KM)':
        'Shows distance travelled by the top 7 vehicles, with total contribution per vehicle and day-wise distance split.',
      'Day and Night Driving Hours':
        'Fleet-level comparison of total driving hours during day and night for each day.',
      'Eco-Driving and Low-Speed Comparision':
        'Compares eco driving hours against low speed hours; ideal vehicles have higher eco hours and lower low-speed hours.',
      'Daily vehicle usage (Day vs Night)':
        'Daily comparison of driving hours split into day, night, and total hours.',
      'Productivity and Downtime':
        'Compares idle time versus productive driving time; ideal vehicles appear with low idle and high usage.',
    };
    return messages[title] || 'View detailed productivity analytics for this metric.';
  };

  // Compute a human readable period string based on filters (e.g., "last 7 days", "last 15 days", or "last X days" for custom ranges)
  const getPeriodLabel = () => {
    const defaultLabel = 'last 7 days';

    try {
      if (!filters || !filters.dateRange) return defaultLabel;

      const dr = filters.dateRange;

      if (dr !== 'Custom') {
        const match = dr.match(/(\d+)/);
        if (match) return `last ${match[1]} days`;
        return `last ${dr.toLowerCase()}`;
      }

      // For custom ranges, compute inclusive day count from startDate and endDate
      if (filters.startDate && filters.endDate) {
        const start = dayjs(filters.startDate);
        const end = dayjs(filters.endDate);
        if (start.isValid() && end.isValid() && !end.isBefore(start)) {
          const diff = end.diff(start, 'day') + 1;
          return `last ${diff} days`;
        }
      }

      return 'selected period';
    } catch (e) {
      return defaultLabel;
    }
  };

  // Wrapper that appends period info for certain titles
  const getProductivityTooltipMessageForTitle = (title: string) => {
    const base = getProductivityTooltipMessage(title);
    const addPeriodTitles = new Set(['Average Median Speed']);

    if (!addPeriodTitles.has(title)) return base;

    return `${base} over the ${getPeriodLabel()}.`;
  };

  // const BarChartData = useMemo(() => {
  //   const warningCountCard = apiData[1203];
  //   console.log('API Data for 1203:', apiData[1203]);

  //   if (warningCountCard && warningCountCard.data) {
  //     const data = warningCountCard.data;
  //     console.log('Raw data for Most Utilised Vehicles:', data);

  //     const labels = data.map((item: any) => Math.round(item.statistical_productivity_score || 0));
  //     const values = data.map((item: any) => item.vehicle_id);

  //     console.log('Labels (utilization):', labels);
  //     console.log('Values (vehicle_ids):', values);

  //     return { labels, values };
  //   }
  //   return undefined;
  // }, [apiData[1203]]);

  const BarChartData = useMemo(() => {
    const warningCountCard = paginatedData1203;
    console.log('API Data for 1203:', paginatedData1203);

    if (warningCountCard && warningCountCard.data) {
      const data = warningCountCard.data;
      console.log('Raw data for Frequent Violators:', data);

      // Check the actual structure of the data
      if (data.length > 0) {
        console.log('First item structure:', data[0]);
        console.log('Keys in first item:', Object.keys(data[0]));
      }

      // Transform based on actual API response structure
      // If data is an array of objects with vehicle_id and statistical_productivity_score
      const labels = data.map((item: any) => item.statistical_productivity_score || 0);
      const values = data.map((item: any) => item.vehicle_id || '');

      console.log('Labels (warnings):', labels);
      console.log('Values (vehicle_ids):', values);

      return { labels, values, totalRecords: warningCountCard.total_records || data.length };
    }
    return undefined;
  }, [paginatedData1203]);

  // const transformedBarData = useMemo(() => {
  //   console.log('BarChartData for transformation:', BarChartData);

  //   if (!BarChartData || !BarChartData.labels || !BarChartData.values) {
  //     console.log('No data available for transformation');
  //     return undefined;
  //   }

  //   const transformed = BarChartData.labels.map((utilizationPercent: number, index: number) => ({
  //     vehicleId: BarChartData.values[index],
  //     warnings: utilizationPercent,
  //   }));

  //   console.log('Transformed Bar Data:', transformed);
  //   return transformed;
  // }, [BarChartData]);

  const transformedBarData = useMemo(() => {
    console.log('BarChartData for transformation:', BarChartData);

    if (!BarChartData || !BarChartData.labels || !BarChartData.values) {
      console.log('No data available for transformation');
      return [];
    }

    // Ensure labels and values have the same length
    const minLength = Math.min(BarChartData.labels.length, BarChartData.values.length);

    const transformed = [];
    for (let i = 0; i < minLength; i++) {
      transformed.push({
        vehicleId: BarChartData.values[i],
        warnings: BarChartData.labels[i], // Assuming labels contains warning counts
      });
    }

    console.log('Transformed Bar Data:', transformed);
    return transformed;
  }, [BarChartData]);

  // const heatmapData = useMemo(() => {
  //   const idleTimeCard = chartConfigs.find((item) => item.title === 'Idle Time by Vehicle');

  //   if (!idleTimeCard) return [];

  //   const chartData = apiData[idleTimeCard.id];

  //   if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
  //     return [];
  //   }

  //   // Extract all data
  //   const rawData: Array<{ record_date: string; vehicle_id: string; idle_time: number }> = chartData.data;

  //   // Get ALL unique vehicle IDs and sort them
  //   const allVehicleIds = Array.from(new Set(rawData.map((item) => item.vehicle_id))).sort();

  //   // Take only FIRST 7 vehicle IDs
  //   const displayVehicleIds = allVehicleIds.slice(0, 7);

  //   // Filter data to include only these 7 vehicle IDs
  //   const filteredData = rawData.filter((item) => displayVehicleIds.includes(item.vehicle_id));

  //   // Transform the filtered data
  //   const result: Array<{
  //     vehicleId: string;
  //     day: string;
  //     value: number;
  //     displayValue: string;
  //     sortDate: string;
  //   }> = [];

  //   const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  //   filteredData.forEach((item) => {
  //     const date = new Date(item.record_date);
  //     const day = `${dayNames[date.getDay()]} ${date.getDate().toString().padStart(2, '0')}`;

  //     result.push({
  //       vehicleId: item.vehicle_id,
  //       day: day,
  //       value: item.idle_time,
  //       displayValue: item.idle_time.toFixed(1),
  //       sortDate: item.record_date,
  //     });
  //   });

  //   return result;
  // }, [apiData, chartConfigs]);

  const heatmapData = useMemo(() => {
    console.log('🔍 Seat Belt Warnings - paginatedData1110:', paginatedData1105);
    if (!paginatedData1105 || !paginatedData1105.data || paginatedData1105.data.length === 0) {
      console.log('❌ No Seat Belt Warnings data available');
      return [];
    }
    const transformed = paginatedData1105.data.map((item: any) => {
      console.log('🦺 Transforming Seat Belt item:', item);
      return {
        vehicleId: item.vehicle_id || '',
        record_date: item.record_date || '',
        idle_time_hours: item.idle_time_hours || 0,
      };
    });

    console.log('✅ Transformed Safety Rating Data:', transformed);
    return transformed;
  }, [paginatedData1105]);

  // Prepare data for Distance Contribution Pie Chart
  const distanceContributionData = useMemo(() => {
    // Directly use paginatedData1604, no need to find chartConfigs
    const chartData = paginatedData1604;

    if (!chartData || !chartData.data || chartData.data.length === 0) {
      console.log('No data for Distance Contribution');
      return null;
    }

    console.log('Distance Contribution Data from paginatedData1604:', chartData.data);

    // Create outer ring data: individual entries with format "Vehicle ID (Date)"
    const outerRingDataItems: Array<{
      alertName: string;
      alertValue: number;
      vehicleId: string;
      date: string;
      percentage: number;
    }> = [];

    // Create inner ring data: combined totals per vehicle
    const innerRingDataItems: Array<{
      alertName: string;
      alertValue: number;
      percentage: number; // This is percentage_overall from API
      vehicleId?: string;
    }> = [];

    chartData.data.forEach((vehicleData: any, vehicleIndex: number) => {
      const vehicleId = vehicleData.vehicle_id;
      const totalDistance = vehicleData.total_distance_km || 0;
      const percentageOverall = vehicleData.percentage_overall || 0;

      // Add to inner ring (vehicle totals) - IMPORTANT: Use percentage_overall from API
      innerRingDataItems.push({
        alertName: vehicleId,
        alertValue: totalDistance,
        percentage: percentageOverall, // This is the correct percentage_overall from API
        vehicleId: vehicleId,
      });

      // Add to outer ring (daily entries)
      if (vehicleData.record_date && vehicleData.distance_km && vehicleData.percentage_day_wise) {
        // Ensure all arrays have same length
        const dates = Array.isArray(vehicleData.record_date) ? vehicleData.record_date : [];
        const distances = Array.isArray(vehicleData.distance_km) ? vehicleData.distance_km : [];
        const percentages = Array.isArray(vehicleData.percentage_day_wise)
          ? vehicleData.percentage_day_wise
          : [];

        const minLength = Math.min(dates.length, distances.length, percentages.length);

        for (let i = 0; i < minLength; i++) {
          const date = dates[i];
          const distance = distances[i] || 0;
          const percentage = percentages[i] || 0;

          outerRingDataItems.push({
            alertName: `${vehicleId} (${date})`,
            alertValue: distance,
            vehicleId: vehicleId,
            date: date,
            percentage: percentage, // This is percentage_day_wise
          });
        }
      }
    });

    // Colors for the charts
    const colors = [
      '#C83333', // Red
      '#F2994A', // Orange
      '#27AE60', // Green
      '#2D9CDB', // Blue
      '#BB6BD9', // Purple
      '#F2C94C', // Yellow
      '#5547DD', // Indigo
      '#E74C3C', // Another Red
      '#9B59B6', // Another Purple
      '#1ABC9C', // Another Green
    ];

    // Calculate total distance for center label
    const totalDistance = innerRingDataItems.reduce((sum, item) => sum + item.alertValue, 0);

    return {
      outerRingData: {
        alertNameWithValueList: outerRingDataItems,
        coloursName: colors.slice(0, outerRingDataItems.length),
        warningTypes: outerRingDataItems.map((item) => item.alertName),
      },
      innerRingData: {
        alertNameWithValueList: innerRingDataItems,
        coloursName: colors.slice(0, innerRingDataItems.length),
        warningTypes: innerRingDataItems.map((item) => item.alertName),
      },
      centerLabel: 'Total Distance\n(in KM)',
      totalValue: totalDistance.toFixed(1) + ' km',
      // Add metadata for percentages
      metadata: {
        hasPercentageData: true,
        innerRingPercentages: innerRingDataItems.map((item) => item.percentage),
      },
    };
  }, [paginatedData1604]); // Only depend on paginatedData1604

  // In ProductivityAnalytics.tsx - Add this data transformation
  const dayNightDrivingData = useMemo(() => {
    const dayNightCard = chartConfigs.find((item) => item.title === 'Day and Night Driving Hours');

    if (!dayNightCard) return [];

    const chartData = apiData[dayNightCard.id];

    if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
      return [];
    }

    // Sort by date
    const sortedData = [...chartData.data].sort(
      (a, b) => new Date(a.record_date).getTime() - new Date(b.record_date).getTime(),
    );

    return sortedData.map((item: any) => ({
      record_date: item.record_date,
      day_driving_hours: item.day_drving_hours || item.day_driving_hours || 0,
      night_driving_hours: item.night_driving_hours || 0,
      total_driving_hours: item.total_driving_hours || 0,
    }));
  }, [apiData, chartConfigs]);

  const scatterChartData = useMemo(() => {
    // Find the scatter chart config by title
    const scatterChartConfig = chartConfigs.find(
      (item) => item.title === 'Eco-Driving and Low-Speed Comparision',
    );

    if (!scatterChartConfig) return [];

    const chartData = apiData[scatterChartConfig.id];

    if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
      return [];
    }

    // Transform the API data to match ScatterDataPoint interface
    return chartData.data.map((item: any, index: number) => {
      const lowSpeedHours = item.low_speed_hours || 0;
      const ecoDrivingHours = item.eco_driving_hours || 0;
      const totalKm = item.total_km_driven || 0;

      // Create vehicle groups for color coding (0-5 groups)
      const vehicleGroup = `Vehicle ${(index % 6) + 1}`;

      // Calculate size based on total_km_driven using your rules
      let size;
      if (totalKm <= 100) {
        size = 100; // 10px * 10px area (100)
      } else if (totalKm > 100 && totalKm <= 200) {
        size = 400; // 20px * 20px area (400)
      } else if (totalKm > 200 && totalKm <= 300) {
        size = 900; // 30px * 30px area (900)
      } else if (totalKm > 300 && totalKm <= 400) {
        size = 1225; // 35px * 35px area (1225)
      } else if (totalKm > 400) {
        size = 1600; // 40px * 40px area (1600)
      } else {
        size = 100; // Default
      }

      return {
        x: lowSpeedHours,
        y: ecoDrivingHours,
        vehicle: vehicleGroup,
        size: size, // This is used for area calculation in CustomDot
        vehicle_id: item.vehicle_id,
        total_km_driven: totalKm,
        originalData: item,
      };
    });
  }, [apiData, chartConfigs]);

  const productivityDowntimeChartData = useMemo(() => {
    // Find the productivity and downtime scatter chart config
    const chartConfig = chartConfigs.find((item) => item.title === 'Productivity and Downtime');

    if (!chartConfig) return [];

    const chartData = apiData[chartConfig.id];

    if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
      return [];
    }

    // Transform the API data to match ScatterDataPoint interface
    return chartData.data.map((item: any, index: number) => {
      const idleTimeHours = item.idle_time_hours || 0;
      const hoursUsed = item.hours_used || 0;
      const distanceKm = item.distance_km || 0;

      // Create vehicle groups for color coding
      const vehicleGroup = `Vehicle ${(index % 6) + 1}`;

      // Calculate size based on distance
      let size;
      if (distanceKm <= 500) {
        size = 100; // 10px * 10px area (100)
      } else if (distanceKm > 500 && distanceKm <= 650) {
        size = 400; // 20px * 20px area (400)
      } else if (distanceKm > 650 && distanceKm <= 750) {
        size = 900; // 30px * 30px area (900)
      } else if (distanceKm > 750 && distanceKm <= 850) {
        size = 1225; // 35px * 35px area (1225)
      } else if (distanceKm > 850) {
        size = 1600; // 40px * 40px area (1600)
      } else {
        size = 100; // Default
      }

      return {
        x: idleTimeHours,
        y: hoursUsed,
        vehicle: vehicleGroup,
        size: size,
        vehicle_id: item.vehicle_id,
        distance_km: distanceKm,
        total_km_driven: distanceKm,
        originalData: item,
      };
    });
  }, [apiData, chartConfigs]);

  const warningPer100KmData = useMemo(() => {
    const chartConfig = chartConfigs.find((item) => item.title === 'Daily vehicle usage (Day vs Night)');
    if (!chartConfig) return [];

    const chartData = apiData[chartConfig.id];
    if (!chartData || !chartData.data || chartData.data.length === 0) {
      return [];
    }

    return chartData.data.map((item: any) => ({
      record_date: item.record_date,
      vehicle_id: item.vehicle_id,
      warning_ratio: item.warning_ratio || 0,
    }));
  }, [apiData, chartConfigs]);

  const DailyEcoDrivingHours = useMemo(() => {
    // First check paginatedData1608, then fall back to apiData
    let dataSource = null;

    if (paginatedData1608?.data?.vehicles && paginatedData1608.data.vehicles.length > 0) {
      dataSource = paginatedData1608;
    } else {
      const chartConfig = chartConfigs.find((item) => item.title === 'Daily Eco-Driving Hours per Vehicle');
      if (chartConfig) {
        const chartData = apiData[chartConfig.id];
        if (chartData && chartData.data && chartData.data.length > 0) {
          dataSource = chartData;
        }
      }
    }

    if (!dataSource) {
      return [];
    }

    // Transform paginatedData1608 format (similar to paginatedData1600)
    if (paginatedData1608?.data?.vehicles) {
      const vehicles = paginatedData1608.data.vehicles;
      const transformedData: any[] = [];

      vehicles.forEach((vehicle: any) => {
        const { vehicle_id, record_date, eco_driving_hours } = vehicle;

        // Match each date with its corresponding eco driving hours
        record_date.forEach((date: string, index: number) => {
          const hours = eco_driving_hours[index];
          // Only include entries with non-null values
          if (hours !== null && hours !== undefined) {
            transformedData.push({
              record_date: date,
              vehicle_id: vehicle_id,
              warning_ratio: hours, // Using warning_ratio field to match CommonFloatingGraph expectation
            });
          }
        });
      });

      return transformedData;
    }

    // Fallback: transform old apiData format
    return dataSource.data.map((item: any) => ({
      record_date: item.record_date,
      vehicle_id: item.vehicle_id,
      warning_ratio: item.eco_driving_hours || 0, // Using warning_ratio field to match CommonFloatingGraph expectation
    }));
  }, [paginatedData1608, apiData, chartConfigs]);

  // Add this useMemo for fleet average if your API provides it
  const ecoDrivingFleetAverageSeries = useMemo(() => {
    const fleet = paginatedData1608?.data?.fleet_average;
    if (!fleet || !fleet.record_date || !fleet.eco_driving_hours) return [];

    const dates: string[] = fleet.record_date || [];
    const avgs: any[] = fleet.eco_driving_hours || [];

    return dates.map((d: string, i: number) => ({
      fullDate: d,
      avg: typeof avgs[i] === 'number' ? parseFloat(avgs[i].toFixed(2)) : 0,
    }));
  }, [paginatedData1608]);

  // Separate transformation for DayNightFloatingGraph (needs day_drving_hours with typo)
  const drivingHoursBreakdownData = useMemo(() => {
    const chartConfig = chartConfigs.find((item) => item.id === 1074);

    if (!chartConfig) return [];

    const chartData = apiData[1074];

    if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
      return [];
    }

    return chartData.data.map((item: any) => ({
      vehicle_id: item.vehicle_id || '',
      day_drving_hours: item.low_speed_hours || 0,
      night_driving_hours: item.eco_driving_hours || 0,
      total_driving_hours: item.total_driving_hours || 0,
    }));
  }, [apiData, chartConfigs]);

  const dailyVehicleUsageData = useMemo(() => {
    const chartConfig = chartConfigs.find((item) => item.title === 'Daily vehicle usage (Day vs Night)');

    if (!chartConfig) {
      console.log('Chart config not found for Daily vehicle usage');
      return [];
    }

    const chartData = apiData[chartConfig.id]; // This should be apiData[1055]

    if (!chartData || !chartData.data || chartData.data.length === 0) {
      console.log('No data available for Daily vehicle usage');
      return [];
    }

    console.log('Daily vehicle usage data (1055):', chartData.data);

    // Directly use the data from API - it's already in correct format
    return chartData.data.map((item: any) => ({
      record_date: item.record_date,
      day_drving_hours: item.day_drving_hours || 0,
      night_driving_hours: item.night_driving_hours || 0,
      total_driving_hours: item.total_driving_hours || 0,
    }));
  }, [apiData, chartConfigs]);

  const dailyTimeBreakdownData = useMemo(() => {
    const chartConfig = chartConfigs.find((item) => item.title === 'Daily time breakdown by vehicle');

    if (!chartConfig) {
      console.log('Chart config not found for Daily time breakdown by vehicle');
      return [];
    }

    const chartData = apiData[chartConfig.id];

    if (!chartData || !chartData.data || chartData.data.length === 0) {
      console.log('No data available for Daily time breakdown by vehicle');
      return [];
    }

    console.log('Daily time breakdown data:', chartData.data);

    // Transform the data to match DailyTimeBreakdownChart expectations
    return chartData.data.map((item: any) => ({
      vehicle_id: item.vehicle_id || '',
      record_date: item.record_date || '',
      total_field_time_hours: item.total_field_time_hours || 0,
      day_driving_hours: item.day_driving_hours || 0,
      night_driving_hours: item.night_driving_hours || 0,
      idle_time_hours: item.idle_time_hours || 0,
      total_driving_hours: (item.day_driving_hours || 0) + (item.night_driving_hours || 0),
    }));
  }, [apiData, chartConfigs]);

  return (
    <>
      {sectionsConfig['productivity-analytics-cards'] !== false && (
        <>
          <Box className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {topCards.map((card, index) => (
              <AnalyticCard
                key={`${card.title}-${index}`}
                title={card.title}
                value={card.value}
                unit={card.unit}
                icon={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      gap: 10,
                      borderRadius: 8,
                      padding: 4,
                      background: '#EAE9F5',
                      opacity: 1,
                      transform: 'rotate(0deg)',
                    }}
                  >
                    <img src={card.icon} alt="icon" style={{ width: 24, height: 24 }} />
                  </span>
                }
                infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
                menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
                trend="up"
                trendValue="2.9%"
                chartData={card.chartData}
                chartId={card.chartId}
                onDataUpdate={onCardDataUpdate}
                tooltipMessage={getProductivityTooltipMessageForTitle(card.title)}
                tableColumns={getProductivityTableColumns(card.chartId)}
                tableData={transformProductivityAnalyticsCardData(
                  productivityCardTableIdMap[card.chartId]?.tableId || 0,
                )}
                tableId={productivityCardTableIdMap[card.chartId]?.tableId}
                paginationInfo={transformPaginationInfo(
                  productivityCardTableIdMap[card.chartId]?.tableId || 0,
                )}
                onTableClick={() => {
                  const tableId = productivityCardTableIdMap[card.chartId]?.tableId;
                  if (tableId && onFetchSingleTableData) {
                    onFetchSingleTableData(tableId);
                  }
                }}
                onTablePaginationChange={(cursor) => {
                  const tableId = productivityCardTableIdMap[card.chartId]?.tableId;
                  if (tableId && onProductivityAnalyticsCardTablePagination) {
                    onProductivityAnalyticsCardTablePagination(tableId, cursor);
                  }
                }}
              />
            ))}
          </Box>
          <Box className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
            {bottomCards.map((card, index) => (
              <AnalyticCard
                key={`${card.title}-${index + 4}`}
                title={card.title}
                value={card.value}
                unit={card.unit}
                icon={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      gap: 10,
                      borderRadius: 8,
                      padding: 4,
                      background: '#EAE9F5',
                      opacity: 1,
                      transform: 'rotate(0deg)',
                    }}
                  >
                    <img src={card.icon} alt="icon" style={{ width: 24, height: 24 }} />
                  </span>
                }
                infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
                menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
                trend="up"
                trendValue="2.9%"
                chartData={card.chartData}
                chartId={card.chartId}
                onDataUpdate={onCardDataUpdate}
                tooltipMessage={getProductivityTooltipMessageForTitle(card.title)}
                tableColumns={getProductivityTableColumns(card.chartId)}
                tableData={transformProductivityAnalyticsCardData(
                  productivityCardTableIdMap[card.chartId]?.tableId || 0,
                )}
                tableId={productivityCardTableIdMap[card.chartId]?.tableId}
                paginationInfo={transformPaginationInfo(
                  productivityCardTableIdMap[card.chartId]?.tableId || 0,
                )}
                onTableClick={() => {
                  const tableId = productivityCardTableIdMap[card.chartId]?.tableId;
                  if (tableId && onFetchSingleTableData) {
                    onFetchSingleTableData(tableId);
                  }
                }}
                onTablePaginationChange={(cursor) => {
                  const tableId = productivityCardTableIdMap[card.chartId]?.tableId;
                  if (tableId && onProductivityAnalyticsCardTablePagination) {
                    onProductivityAnalyticsCardTablePagination(tableId, cursor);
                  }
                }}
              />
            ))}
          </Box>
        </>
      )}

      <Box
        className={`mt-4 ${
          sectionsConfig['productivity-gauge-meter'] === false ||
          sectionsConfig['productivity-distance-contribution'] === false
            ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
            : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
        }`}
      >
        {/* <AnalyticCard
          title="Speed Fluctuation"
          value="100%"
          unit=""
          icon={
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                gap: 10,
                borderRadius: 8,
                padding: 4,
                background: '#EAE9F5',
                opacity: 1,
                transform: 'rotate(0deg)',
              }}
            >
            </span>
          }
          infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
          menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
          trend="up"
          trendValue="2.9%"
          chartData={[]}
        /> */}
        {sectionsConfig['productivity-gauge-meter'] !== false && (
          <CommonGaugeMeterChart
            title="Driver Productivity Index"
            containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
            value={complianceScore}
            infoIcon={
              <Tooltip
                title="A weighted score (0–100) measuring vehicle productivity based on utilization, eco driving, idle time, and overspeeding behaviour"
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
              </Tooltip>
            }
            customGaugeLevels={[
              {
                label: 'Low',
                range: '00-20',
                min: 0,
                max: 20,
                color: '#C83333',
              },
              {
                label: 'Moderate',
                range: '20-40',
                min: 20,
                max: 40,
                color: '#F2994A',
              },
              {
                label: 'High',
                range: '40-75',
                min: 40,
                max: 100,
                color: '#16A34A',
              },
            ]}
          />
        )}
        {/* In the ProductivityAnalytics JSX - Distance Contribution Chart Section */}
        {sectionsConfig['productivity-distance-contribution'] !== false && (
          <CommonDoublePieChart
            title="Distance Contribution by Vehicle & Date (in KM)"
            outerRingData={distanceContributionData?.outerRingData}
            innerRingData={distanceContributionData?.innerRingData}
            centerLabel={distanceContributionData?.centerLabel}
            infoIcon={
              <Tooltip
                title="Shows distance travelled by the top 7 vehicles, with total contribution per vehicle and day-wise distance split."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            containerClassName="flex sm:flex-row w-full items-center flex-col gap-4"
            selectionBox={true}
            tableTitle="Table View – Distance Contribution by Vehicle & Date (in KM)"
            tableHeaderText="Detailed breakdown of distance contribution by vehicle and date"
            tableColumns={getProductivityTableColumns(1303)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1303]?.tableId || 0)}
            // Table pagination (different from chart pagination)
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1303]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1303]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1303]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
            // Chart-level pagination for 1604 (SAME AS BarChartCommon)
            paginatedData={paginatedData1604}
            onChartPaginationChange={(cursor) => {
              if (onPaginationChange1604) onPaginationChange1604(cursor);
            }}
            // Add paginationInfo for chart (similar to BarChartCommon)
            chartPaginationInfo={{
              currentPage: 1, // You'll need to calculate this based on offset
              pageSize: 7, // 7 vehicles per page based on your data
              totalRecords: paginatedData1604?.total_records || 0,
              hasMorePages: !!paginatedData1604?.next_cursor,
              nextCursor: paginatedData1604?.next_cursor,
              previousCursor: paginatedData1604?.previous_cursor || null,
            }}
            chartId={1604}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
      </Box>
      <Box
        className={`mt-4 ${
          sectionsConfig['productivity-idle-time'] === false ||
          sectionsConfig['productivity-utilised-vehicles'] === false
            ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
            : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
        }`}
      >
        {sectionsConfig['productivity-idle-time'] !== false && (
          <ProductivityHeatMapChart
            title="Idle Time by Vehicle"
            data={heatmapData}
            containerClassName="w-full"
            infoIcon={
              <Tooltip title="Daily idle time comparison for the top 7 vehicles." arrow>
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableTitle="Table View – Idle Time by Vehicle"
            tableHeaderText="Detailed breakdown of idle time by vehicle and date"
            tableColumns={getProductivityTableColumns(1300)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1300]?.tableId || 0)}
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1300]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1300]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1300]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
            // Server-side day pagination (heatmap) using paginatedData1105
            dayPaginationInfo={
              paginatedData1105
                ? {
                    lastEvaluatedKey: paginatedData1105.next_cursor,
                    next_cursor: paginatedData1105.next_cursor,
                  }
                : undefined
            }
            onDayPaginationChange={(cursor) => {
              if (onPaginationChange1105) onPaginationChange1105(cursor);
            }}
            chartId={1105}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
        {sectionsConfig['productivity-utilised-vehicles'] !== false && (
          <BarChartCommon
            dayWiseIdealTimeResponses={transformedBarData}
            HeaderName=" Most Utilised Vehicles"
            showFullName={true}
            sideTitle="Utilisation (%)"
            DownTitle="Vehicle ID"
            infoIcon={
              <Tooltip
                title="Vehicles ranked by utilization percentage (driving time divided by total field time)."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableColumns={getProductivityTableColumns(1203)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1203]?.tableId || 0)}
            tableTitle="Table View – Most Utilised Vehicles"
            tableHeaderText="Detailed breakdown of vehicle utilization"
            tooltipLabel="Utilization"
            // New: support chart-level pagination similar to Frequent Violators
            paginatedData={paginatedData1203}
            onPaginationChange={(cursor, direction) => {
              console.log('📄 Pagination change requested:', { cursor, direction });
              if (onPaginationChange1203) {
                onPaginationChange1203(cursor);
              }
            }}
            // Use the same 'paginationInfo' shape used in AnalyticsCompilance for chart-level pagination
            paginationInfo={{
              currentPage: paginatedData1203?.currentPage || 1,
              pageSize: 30,
              totalRecords: paginatedData1203?.total_records || transformedBarData.length,
              hasMorePages: !!paginatedData1203?.next_cursor,
              nextCursor: paginatedData1203?.next_cursor,
              previousCursor: paginatedData1203?.previous_cursor,
            }}
            useFleetTableStyle={true}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1203]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onTablePaginationChange={(newPage: number) => {
              const tableId = productivityCardTableIdMap[1203]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                // Store current page's cursor before fetching next page (match AnalyticsCompilanceScreen behaviour)
                const currentCursor = productivityAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                setMostUtilisedCursors((prev: any) => ({
                  ...prev,
                  [`page_${newPage}`]: currentCursor,
                }));
                // Fetch next page using the cursor for current page
                onProductivityAnalyticsCardTablePagination(tableId, currentCursor);
              }
            }}
            chartId={1203}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
      </Box>
      <Box
        className={`mt-4 ${
          sectionsConfig['productivity-day-night-driving'] === false ||
          sectionsConfig['productivity-driving-breakdown'] === false
            ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
            : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
        }`}
      >
        {sectionsConfig['productivity-day-night-driving'] !== false && (
          <BarChartCommon
            dayNightDrivingData={dayNightDrivingData}
            HeaderName="Day and Night Driving Hours"
            chartType="day-night-hours"
            sideTitle="Driving Hours"
            infoIcon={
              <Tooltip
                title="Fleet-level comparison of total driving hours during day and night for each day."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableColumns={getProductivityTableColumns(1057)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1057]?.tableId || 0)}
            tableTitle="Table View – Day and Night Driving Hours"
            tableHeaderText="Detailed breakdown of day vs night driving hours"
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1057]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1057]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onTablePaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1057]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
            chartId={1057}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
        {sectionsConfig['productivity-driving-breakdown'] !== false && (
          <DayNightFloatingGraph
            title="Driving Hours Breakdown"
            data={drivingHoursBreakdownData}
            chartId={1074}
            onCardDataUpdate={(chartId, startDate, endDate) => {
              console.log('Update requested for chart:', chartId, startDate, endDate);
              // Handle optional parameters
              if (onCardDataUpdate && chartId && startDate && endDate) {
                onCardDataUpdate(chartId, startDate, endDate);
              }
            }}
            infoIcon={
              <Tooltip
                title="Distribution of driving hours across speed categories (eco and low speed), with totals matching overall driving time."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableColumns={getProductivityTableColumns(1074)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1074]?.tableId || 0)}
            tableTitle="Table View – Driving Hours Breakdown"
            tableHeaderText="Driving Hours Breakdown"
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1074]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1074]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1074]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
          />
        )}
      </Box>
      <Box
        className={`mt-4 ${
          sectionsConfig['productivity-daily-usage'] === false ||
          sectionsConfig['productivity-eco-daily'] === false
            ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
            : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
        }`}
      >
        {sectionsConfig['productivity-daily-usage'] !== false && (
          <DayNightFloatingGraph
            title="Daily vehicle usage (Day vs Night)"
            data={dailyVehicleUsageData}
            chartId={1055}
            onCardDataUpdate={(chartId, startDate, endDate) => {
              console.log('Update requested for chart:', chartId, startDate, endDate);
              // Handle optional parameters
              if (onCardDataUpdate && chartId && startDate && endDate) {
                onCardDataUpdate(chartId, startDate, endDate);
              }
            }}
            infoIcon={
              <Tooltip
                title="Daily comparison of driving hours split into day, night, and total hours."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableColumns={getProductivityTableColumns(1055)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1055]?.tableId || 0)}
            tableTitle="Table View – Daily vehicle usage (Day vs Night)"
            tableHeaderText="Detailed breakdown of daily vehicle usage by date"
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1055]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1055]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1055]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
          />
        )}
        {sectionsConfig['productivity-eco-daily'] !== false && (
          <CommonFloatingGraph
            title="Daily Eco-Driving Hours per Vehicle"
            data={DailyEcoDrivingHours}
            fleetAverage={ecoDrivingFleetAverageSeries}
            chartId={1608}
            onCardDataUpdate={(chartId, startDate, endDate) => {
              console.log('Update requested for chart:', chartId, startDate, endDate);
              // Handle optional parameters
              if (onCardDataUpdate && chartId && startDate && endDate) {
                onCardDataUpdate(chartId, startDate, endDate);
              }
            }}
            yAxisLabel="Eco Driving Hours"
            infoIcon={
              <Tooltip title="Eco driving hours tracked per vehicle on a daily basis." arrow>
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableColumns={getProductivityTableColumns(1304)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1304]?.tableId || 0)}
            tableTitle="Table View – Daily Eco-Driving Hours per Vehicle"
            tableHeaderText="Detailed breakdown of eco-driving hours by vehicle and date"
            paginationInfo={{
              currentPage: paginatedData1608?.next_cursor
                ? paginatedData1608?.next_cursor?.offset
                  ? Math.floor(paginatedData1608.next_cursor.offset / 30) + 1
                  : 1
                : 1,
              pageSize: 30,
              totalRecords: paginatedData1608?.data?.vehicles?.length || 0,
              hasMorePages: !!paginatedData1608?.next_cursor,
              nextCursor: paginatedData1608?.next_cursor,
              previousCursor: paginatedData1608?.previous_cursor,
            }}
            paginatedData={paginatedData1608}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1304]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              if (onPaginationChange1608) onPaginationChange1608(cursor);
            }}
            onTablePaginationChange={(newPage: number) => {
              const tableId = productivityCardTableIdMap[1304]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                // Store current page's cursor before fetching next page
                const currentCursor = productivityAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                // You might want to add state for storing cursors similar to warningPer100KmCursors
                // setWarningPer100KmCursors((prev: any) => ({
                //   ...prev,
                //   [`page_${newPage}`]: currentCursor,
                // }));
                onProductivityAnalyticsCardTablePagination(tableId, currentCursor);
              }
            }}
          />
        )}
      </Box>
      <Box
        className={`mt-4 ${
          sectionsConfig['productivity-downtime-scatter'] === false ||
          sectionsConfig['productivity-eco-driving-scatter'] === false
            ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
            : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
        }`}
      >
        {sectionsConfig['productivity-downtime-scatter'] !== false && (
          <CommonScatterChart
            title="Productivity and Downtime"
            data={productivityDowntimeChartData}
            xAxisLabel="Hours Used"
            yAxisLabel="Idle Time/Day"
            infoIcon={
              <Tooltip
                title="Compares idle time versus productive driving time; ideal vehicles appear with low idle and high usage."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            containerClassName="w-full"
            tableColumns={getProductivityTableColumns(1066)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1066]?.tableId || 0)}
            tableTitle="Table View – Productivity and Downtime"
            tableHeaderText="Detailed breakdown of idle time and productivity hours by vehicle"
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1066]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1066]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1066]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
            chartId={1066}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
        {sectionsConfig['productivity-eco-driving-scatter'] !== false && (
          <CommonScatterChart
            title="Eco-Driving and Low-Speed Comparision"
            data={scatterChartData}
            xAxisLabel="Low Speed Hours"
            yAxisLabel="Eco Driving Hours"
            infoIcon={
              <Tooltip
                title="Compares eco driving hours against low speed hours; ideal vehicles have higher eco hours and lower low-speed hours."
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            containerClassName="w-full"
            tableColumns={getProductivityTableColumns(1076)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1076]?.tableId || 0)}
            tableTitle="Table View – Eco-Driving and Low-Speed Comparision"
            tableHeaderText="Detailed breakdown of eco-driving and low-speed hours by vehicle"
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1076]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1076]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1076]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
            chartId={1076}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
      </Box>
      {/* <Box className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 items-end gap-4">
        {sectionsConfig['productivity-daily-breakdown'] !== false && (
          <DailyTimeBreakdownChart
            title="Daily time breakdown by vehicle"
            data={dailyTimeBreakdownData}
            containerClassName="w-full"
            // eyeIcon={<img src={InfoIcon} alt="Eye" style={{ width: 24, height: 24, cursor: 'pointer' }} />}
            menuIcon={<img src={menuIcon} alt="Menu" style={{ width: 24, height: 24, cursor: 'pointer' }} />}
            infoIcon={
              <Tooltip
                title="Shows daily time breakdown by vehicle including day, night, and idle hours"
                arrow
              >
                <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              </Tooltip>
            }
            tableColumns={getProductivityTableColumns(1070)}
            tableData={transformProductivityAnalyticsCardData(productivityCardTableIdMap[1070]?.tableId || 0)}
            tableTitle="Daily Time Breakdown"
            tableHeaderText="Daily Time Breakdown by Vehicle"
            paginationInfo={transformPaginationInfo(productivityCardTableIdMap[1070]?.tableId || 0)}
            onTableIconClick={() => {
              const tableId = productivityCardTableIdMap[1070]?.tableId;
              if (tableId && onFetchSingleTableData) {
                onFetchSingleTableData(tableId);
              }
            }}
            onPaginationChange={(cursor) => {
              const tableId = productivityCardTableIdMap[1070]?.tableId;
              if (tableId && onProductivityAnalyticsCardTablePagination) {
                onProductivityAnalyticsCardTablePagination(tableId, cursor);
              }
            }}
            chartId={1070}
            onCardDataUpdate={onCardDataUpdate}
          />
        )}
      </Box> */}
    </>
  );
};

export default ProductivityAnalytics;
